import { useCallback, useEffect, useMemo, useState } from "react";
import {
  supabaseAuthenticatedApi,
  workspaceAgentUrl,
} from "../components/SupabaseClient";
import { useProfile } from "../context/ProfileContext";

const PARENT_ROW = { name: "..", type: "dir", _isParent: true };

function getParentPath(path) {
  const parts = path.split("/").filter(Boolean);
  parts.pop();
  return parts.length === 0 ? "." : parts.join("/");
}

function normalizeRows(path, entries) {
  const isRoot = path === "." || path === "";
  return isRoot ? entries : [PARENT_ROW, ...entries];
}

export function useRemoteDirectoryBrowser({
  initialPath = ".",
  source = "home",
} = {}) {
  const { profile } = useProfile();
  const username = profile?.profile?.efabric_username;
  const userid = profile?.profile?.efabric_userid;

  const [currentPath, setCurrentPath] = useState(initialPath);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [lastSuccessfulState, setLastSuccessfulState] = useState({
    path: initialPath,
    rows: [],
  });

  const fetchDir = useCallback(
    async (path, isActive = () => true) => {
      if (!userid) return;
      setLoading(true);
      setError(null);

      try {
        const response = await supabaseAuthenticatedApi.post(
          "/filesystem/list-directory",
          { scope: source, path },
          workspaceAgentUrl,
          5000,
        );
        const data = await response.json();
        // Guard prevents a stale/slow response from overwriting newer state or
        // calling setState after unmount.
        if (!isActive()) return;
        const entries = data.entries || [];
        const resolvedPath = data.path || path;
        const normalizedRows = normalizeRows(resolvedPath, entries);

        setCurrentPath(resolvedPath);
        setRows(normalizedRows);
        setTruncated(data.truncated || false);
        setLastSuccessfulState({
          path: resolvedPath,
          rows: normalizedRows,
        });
      } catch (fetchError) {
        if (!isActive()) return;
        setError(fetchError.message || "Failed to load directory");
      } finally {
        if (isActive()) setLoading(false);
      }
    },
    [source, userid],
  );

  useEffect(() => {
    let active = true;

    if (userid) {
      fetchDir(initialPath, () => active);
    } else {
      setCurrentPath(initialPath);
      setRows([]);
      setTruncated(false);
      setLoading(false);
      setError(null);
      setLastSuccessfulState({
        path: initialPath,
        rows: [],
      });
    }

    return () => {
      active = false;
    };
  }, [fetchDir, initialPath, source, userid]);

  const displayPath = useMemo(() => {
    return currentPath === "." || currentPath === "" ? "~" : `~/${currentPath}`;
  }, [currentPath]);

  const resolveRowPath = useCallback(
    (row) => {
      if (row._isParent) {
        return getParentPath(currentPath);
      }

      if (currentPath === "." || currentPath === "") {
        return row.name;
      }

      return `${currentPath}/${row.name}`;
    },
    [currentPath],
  );

  const openRow = useCallback(
    (row) => {
      if (row.type !== "dir") return false;
      fetchDir(resolveRowPath(row));
      return true;
    },
    [fetchDir, resolveRowPath],
  );

  const refresh = useCallback(() => {
    fetchDir(currentPath);
  }, [currentPath, fetchDir]);

  const recoverFromError = useCallback(() => {
    setCurrentPath(lastSuccessfulState.path);
    setRows(lastSuccessfulState.rows);
    setError(null);
  }, [lastSuccessfulState.path, lastSuccessfulState.rows]);

  return {
    currentPath,
    displayPath,
    error,
    fetchDir,
    loading,
    openRow,
    recoverFromError,
    refresh,
    resolveRowPath,
    rows,
    truncated,
    userid,
    username,
  };
}
